/**
 * LIFECYCLE-RENDER-V1 (V1-P2) - pure view-model for the canonical Lead 360
 * lifecycle STAGE rail + next-action card.
 *
 * This module RENDERS the existing lifecycle engine
 * (lib/domain/lead/lead-lifecycle.ts) - it never forks its logic. The engine is
 * the single source of truth for the ladder ORDER, the per-stage OUTCOME /
 * REASON / NEXT-ACTION contract, and which stages are disposition-derived; this
 * file is its frontend presentation mirror.
 *
 * WHY A LOCAL MIRROR (no cross-import). clinic-os ships the backend (`lib/**`,
 * root tsconfig) and the frontend (`frontend/**`, its own tsconfig) as SEPARATE
 * TypeScript packages, and the frontend deliberately does not import repo-root
 * `lib/` into its bundle (`@` resolves only to `./src`; tsconfig.app include is
 * `src`). So the stage vocabulary is mirrored here and kept in LOCKSTEP with the
 * engine by a ROOT equivalence pin,
 * `tests/unit/lead-lifecycle-rail-view-equivalence.test.ts`, which imports BOTH
 * the real engine AND this module and fails the build if any label, next-action,
 * order, rank, or disposition-derived membership drifts. That pin - not a copied
 * string - is what guarantees the next-action card text IS
 * `STAGE_CONTRACTS[stage].nextAction`. Same posture, and same equivalence-pin
 * precedent, as `frontend/src/dashboard/role-view.ts`.
 *
 * PURE + DETERMINISTIC: no React, no fetch, no `Date.now()`, no mutation. Pure so
 * the root vitest suite (`environment: 'node'`) can import it for the pin without
 * dragging in JSX or the `@` alias (only TYPE imports reference the contract, and
 * those are erased at runtime).
 *
 * CANON STAGE vs LINKAGE COMPLETENESS - the V1-P2 fix. The lifecycle STAGE
 * (`Lead360Stage`, from leads.enquiry_status) is the forward-only ladder. It is
 * DISTINCT from `Lead360LifecycleCompleteness` (linkage/identity completeness:
 * "Source attributed" / "Walk-in linked" / ...). The founder-evidenced mixup was
 * a linkage-completeness label rendered AS the lifecycle stage; nothing in this
 * module ever reads the linkage hops.
 */

// Relative (not `@/`) so the ROOT equivalence pin can import this module without
// the frontend-only `@` alias - same cross-boundary posture as role-view.ts.
import type { Lead360Event, Lead360Stage } from '../owner/lead-360-view';

// -- Ladder order + rank (mirror of LEAD_STAGES / LEAD_STAGE_RANK) ------------

/**
 * The forward-only ordered ladder, index = forward rank. Mirror of the engine
 * `LEAD_STAGES` (lib/domain/lead/lead-lifecycle.ts:50-59). `lost` is the terminal
 * tail reachable from any non-terminal stage - it is rendered as a terminal
 * DISPOSITION tail, not as rank 7 on the forward path (see `buildStageRailModel`).
 */
export const LEAD_STAGE_ORDER: readonly Lead360Stage[] = [
  'new',
  'contacted',
  'qualified',
  'appointment_booked',
  'arrived',
  'patient',
  'converted_to_revenue',
  'lost',
];

/** Forward rank per stage (mirror of the engine `LEAD_STAGE_RANK`). */
export const LEAD_STAGE_RANK: Readonly<Record<Lead360Stage, number>> = {
  new: 0,
  contacted: 1,
  qualified: 2,
  appointment_booked: 3,
  arrived: 4,
  patient: 5,
  converted_to_revenue: 6,
  lost: 7,
};

/**
 * The forward stages shown as the ladder (everything except the `lost` terminal
 * tail). `lost` is rendered separately as a disposition tail because it is
 * reachable from ANY non-terminal stage - placing it inline at rank 7 would
 * falsely mark every earlier stage as "done" for a lead that was lost early.
 */
export const FORWARD_STAGES: readonly Lead360Stage[] = LEAD_STAGE_ORDER.filter(
  (s) => s !== 'lost',
);

// -- Per-stage contract labels (mirror of STAGE_CONTRACTS :258-330) -----------

/** Controlled outcome label per stage - mirror of `STAGE_CONTRACTS[s].outcome`. */
export const STAGE_OUTCOME_LABEL: Readonly<Record<Lead360Stage, string>> = {
  new: 'New lead',
  contacted: 'Contacted',
  qualified: 'Qualified',
  appointment_booked: 'Appointment booked',
  arrived: 'Arrived',
  patient: 'Patient',
  converted_to_revenue: 'Converted to revenue',
  lost: 'Lost',
};

/**
 * Controlled next-action per stage - mirror of `STAGE_CONTRACTS[s].nextAction`.
 * The pin test asserts this map is byte-identical to the engine, so the
 * next-action card text provably IS the engine contract, never a re-typed string.
 */
export const STAGE_NEXT_ACTION: Readonly<Record<Lead360Stage, string>> = {
  new: 'Make first contact within SLA',
  contacted: 'Qualify the lead from the disposition',
  qualified: 'Offer a slot and book the appointment',
  appointment_booked: 'Send the appointment reminder',
  arrived: 'Complete check-in and intake',
  patient: 'Proceed to consultation (gate owned by COC; deferred)',
  converted_to_revenue: 'Request a review and schedule recall',
  lost: 'Review the lost reason; no further pursuit',
};

/**
 * Stages that are NOT advanced to directly - they are DERIVED from the reversible
 * contact-disposition axis and carry no single `STAGE_ENTRY_EVENT` in the engine.
 * Mirror of the engine truth (`contacted` is absent from `STAGE_ENTRY_EVENT`,
 * lib/domain/lead/lead-lifecycle.ts:109-118; the stage route rejects a direct
 * advance to it, api/routes/lead-stage.ts:74-76 `DISPOSITION_DERIVED_STAGES`).
 * The rail renders these as derived (an attempt count), never as an advance
 * affordance.
 */
export const DISPOSITION_DERIVED_STAGES: readonly Lead360Stage[] = ['contacted'];

/** Type guard: true only for a value in the controlled stage set. */
export function isRailStage(v: unknown): v is Lead360Stage {
  return (
    typeof v === 'string' &&
    (LEAD_STAGE_ORDER as readonly string[]).includes(v)
  );
}

/** A stage with no outgoing forward transitions (journey end). */
export function isTerminalRailStage(s: Lead360Stage): boolean {
  return s === 'converted_to_revenue' || s === 'lost';
}

/** True for a disposition-derived stage (rendered as a derived attempt count). */
export function isDispositionDerivedStage(s: Lead360Stage): boolean {
  return (DISPOSITION_DERIVED_STAGES as readonly string[]).includes(s);
}

// -- Contact-attempt derivation (the `contacted` count) ----------------------

/**
 * Activity-stream event types that count as a CONTACT ATTEMPT for the derived
 * `contacted` node. These are the two disposition->event reuses the engine keeps
 * (`answered` -> `called`, `no_answer` -> `not_answered`,
 * lib/domain/lead/lead-lifecycle.ts:170-180) and that also exist on the
 * frontend activity vocabulary (`Lead360EventType`). KNOWN LIMIT (recorded, not
 * hidden): the other five disposition tokens (wrong_number / call_later /
 * interested / not_interested / just_checking) ARE on the activity vocabulary
 * (the mirror is full-backend-lockstep since the
 * OMNICHANNEL-TIMELINE-AND-LEAD-360-V1 corrective) but are deliberately NOT
 * counted here -- widening the counted set is a product decision, never a
 * vocabulary side effect; the count stays honestly "attempts visible in the
 * activity stream", never a fabricated total.
 */
export const CONTACT_ATTEMPT_EVENT_TYPES: ReadonlySet<string> = new Set([
  'called',
  'not_answered',
]);

/** Count contact attempts visible in the activity stream. Pure. */
export function countContactAttempts(
  events: readonly Lead360Event[] | undefined,
): number {
  return (events ?? []).filter((e) =>
    CONTACT_ATTEMPT_EVENT_TYPES.has(e.eventType),
  ).length;
}

// -- The rail model ----------------------------------------------------------

/** Designed rail node states (product-quality UI bar). */
export type StageRailNodeState = 'done' | 'current' | 'future' | 'disposition';

export interface StageRailNode {
  readonly stage: Lead360Stage;
  /** Engine-canon outcome label (STAGE_OUTCOME_LABEL[stage]). */
  readonly label: string;
  readonly state: StageRailNodeState;
  /** True for `contacted` - render as a derived attempt count, never an advance. */
  readonly derived: boolean;
  /** True for a terminal stage (converted_to_revenue / lost). */
  readonly terminal: boolean;
  /** True when this is the lead's committed current stage. */
  readonly active: boolean;
  /** For `contacted` only: attempts visible in the activity stream; null otherwise. */
  readonly attemptCount: number | null;
}

export interface StageRailModel {
  /** True when the wire carried a valid canon stage. False => honest empty state. */
  readonly known: boolean;
  readonly currentStage: Lead360Stage | null;
  /** Engine-canon outcome label for the current stage, or null. */
  readonly currentLabel: string | null;
  /** STAGE_CONTRACTS[current].nextAction (engine-sourced via the pinned map), or null. */
  readonly nextAction: string | null;
  readonly isLost: boolean;
  /** The forward ladder (new .. converted_to_revenue), in order. */
  readonly ladder: readonly StageRailNode[];
  /** The `lost` terminal disposition tail (rendered distinct from the ladder). */
  readonly terminalNode: StageRailNode;
}

function ladderNodeState(
  stage: Lead360Stage,
  currentStage: Lead360Stage | null,
  isLost: boolean,
): StageRailNodeState {
  // When the lead is LOST, its pre-lost stage is not recoverable from
  // enquiry_status alone (the CAS overwrote it), so the forward ladder is
  // honestly indeterminate - every node reads 'future', and only the terminal
  // Lost tail is active. Never claim a stage was completed we cannot prove.
  if (isLost || currentStage === null) return 'future';
  const rank = LEAD_STAGE_RANK[stage];
  const currentRank = LEAD_STAGE_RANK[currentStage];
  if (rank < currentRank) return 'done';
  if (stage === currentStage) return 'current';
  return 'future';
}

/**
 * Build the pure rail model for a committed stage token + the lead's activity
 * stream. Honest by construction: an absent / non-canon stage yields
 * `known: false` (the UI renders "stage not recorded", never a fabricated stage);
 * a lost lead shows an indeterminate ladder plus an active terminal tail.
 */
export function buildStageRailModel(
  stage: Lead360Stage | null | undefined,
  events: readonly Lead360Event[] | undefined,
): StageRailModel {
  const known = isRailStage(stage);
  const currentStage: Lead360Stage | null = known ? stage : null;
  const isLost = currentStage === 'lost';
  const attempts = countContactAttempts(events);

  const ladder: StageRailNode[] = FORWARD_STAGES.map((s) => {
    const derived = isDispositionDerivedStage(s);
    return {
      stage: s,
      label: STAGE_OUTCOME_LABEL[s],
      state: ladderNodeState(s, currentStage, isLost),
      derived,
      terminal: isTerminalRailStage(s),
      active: currentStage !== null && !isLost && s === currentStage,
      attemptCount: derived ? attempts : null,
    };
  });

  const terminalNode: StageRailNode = {
    stage: 'lost',
    label: STAGE_OUTCOME_LABEL.lost,
    state: isLost ? 'disposition' : 'future',
    derived: false,
    terminal: true,
    active: isLost,
    attemptCount: null,
  };

  return {
    known,
    currentStage,
    currentLabel: currentStage !== null ? STAGE_OUTCOME_LABEL[currentStage] : null,
    nextAction: currentStage !== null ? STAGE_NEXT_ACTION[currentStage] : null,
    isLost,
    ladder,
    terminalNode,
  };
}

/**
 * Engine-canon lifecycle stage label for the compact highlights row / any
 * one-line stage display. Honest fallback when the stage is absent / non-canon:
 * "Stage not recorded" - NEVER a linkage-completeness word (that was the mixup).
 */
export const STAGE_NOT_RECORDED_LABEL = 'Stage not recorded';

export function railStageLabel(stage: Lead360Stage | null | undefined): string {
  return isRailStage(stage) ? STAGE_OUTCOME_LABEL[stage] : STAGE_NOT_RECORDED_LABEL;
}

/** Engine-canon next-action for a one-line display; honest fallback when unknown. */
export const NEXT_ACTION_UNKNOWN_LABEL = 'Next action set once the stage is recorded';

export function railNextActionLabel(stage: Lead360Stage | null | undefined): string {
  return isRailStage(stage) ? STAGE_NEXT_ACTION[stage] : NEXT_ACTION_UNKNOWN_LABEL;
}
